// src/components/dashboards/AdminDashboard.jsx
// Changes from original:
//   1. Import PromoteStudents
//   2. Add 'promote-students' tab button
//   3. Add promote-students tab panel

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import { adminAPI, calendarAPI, mbbsAPI, internAPI } from '../../services/api';
import Calendar from '../shared/Calendar';
import '../../styles/Dashboard.css';
import '../../styles/ModalStyles.css';
import AdminLeaveApproval from '../admin/AdminLeaveApproval';
import AdminLeavePolicyManager from '../admin/AdminLeavePolicyManager';
import ShiftManagement from '../admin/ShiftManagement';

import AdminSettings from '../admin/AdminSettings';
import PromoteStudents from '../admin/PromoteStudents';   // ← NEW
import AdminCMPermissions from '../admin/AdminCMPermissions';
import MyBorrowedBooks from '../shared/MyBorrowedBooks';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import StudentActivityModal from '../professor/activities/modals/StudentActivityModal';
import { UserModal as PrincipalUserModal } from './PrincipalDashboard';
import { hostelInventoryAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Admin Subtab Analytics Components                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
/*  AdminFinancialAnalyticsTab                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */
function AdminFinancialAnalyticsTab({ data }) {
  const [revenueCategory, setRevenueCategory] = useState('HOSPITAL');
  const [viewMode, setViewMode] = useState('dashboard');
  const [txnSearch, setTxnSearch] = useState('');

  const catFin = data?.categorized_financials || {};

  // Helper to extract only paid/collected transactions
  const filterPaidOnly = (txnsList) => {
    return (txnsList || []).filter(t => t.status === 'PAID' || t.status === 'SUCCESS' || t.status === 'COMPLETED');
  };

  const hospTxns = filterPaidOnly(catFin.hospital_data?.transactions);
  const collTxns = filterPaidOnly(catFin.college_data?.transactions);
  const hostTxns = filterPaidOnly(catFin.hostel_data?.transactions);

  const hospTotalCollected = hospTxns.reduce((acc, t) => acc + (t.amount_val || 0), 0);
  const collTotalCollected = collTxns.reduce((acc, t) => acc + (t.amount_val || 0), 0);
  const hostTotalCollected = hostTxns.reduce((acc, t) => acc + (t.amount_val || 0), 0);

  let activeData = {
    total_revenue: `₹${hospTotalCollected.toLocaleString()}`,
    completed_revenue: `₹${hospTotalCollected.toLocaleString()}`,
    pending_revenue: '₹0',
    transaction_count: hospTxns.length,
    success_rate: '100%',
    transactions: hospTxns
  };

  if (revenueCategory === 'COLLEGE') {
    activeData = {
      total_revenue: `₹${collTotalCollected.toLocaleString()}`,
      completed_revenue: `₹${collTotalCollected.toLocaleString()}`,
      pending_revenue: '₹0',
      transaction_count: collTxns.length,
      success_rate: '100%',
      transactions: collTxns
    };
  } else if (revenueCategory === 'HOSTEL') {
    activeData = {
      total_revenue: `₹${hostTotalCollected.toLocaleString()}`,
      completed_revenue: `₹${hostTotalCollected.toLocaleString()}`,
      pending_revenue: '₹0',
      transaction_count: hostTxns.length,
      success_rate: '100%',
      transactions: hostTxns
    };
  } else if (revenueCategory === 'ALL') {
    const allTxns = [...hospTxns, ...collTxns, ...hostTxns];
    const allTotalCollected = hospTotalCollected + collTotalCollected + hostTotalCollected;
    activeData = {
      total_revenue: `₹${allTotalCollected.toLocaleString()}`,
      completed_revenue: `₹${allTotalCollected.toLocaleString()}`,
      pending_revenue: '₹0',
      transaction_count: allTxns.length,
      success_rate: '100%',
      transactions: allTxns
    };
  }

  const txns = activeData.transactions || [];

  const filteredTxns = txns.filter(t => {
    const cust = t.customer || t.name || '';
    const src = t.source || t.course || '';
    return !txnSearch || cust.toLowerCase().includes(txnSearch.toLowerCase()) || t.id.toLowerCase().includes(txnSearch.toLowerCase()) || src.toLowerCase().includes(txnSearch.toLowerCase());
  });

  const exportCSV = () => {
    if (!filteredTxns.length) {
      notify.info("No transaction records available to export for this category.");
      return;
    }
    const csvContent = "data:text/csv;charset=utf-8," 
      + ["Transaction ID,Date,Customer/Patient/Student,Revenue Source,Amount,Method,Status", ...filteredTxns.map(e => `"${e.id}","${e.date}","${e.customer || e.name || ''}","${e.source || e.course || ''}","${String(e.amount).replace(/,/g,'')}","${e.method}","${e.status}"`)].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `${revenueCategory}_Collected_Revenue_${viewMode}_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header Bar with Category Selector & View Switcher */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Revenue Category:</label>
          <select 
            value={revenueCategory} 
            onChange={e => setRevenueCategory(e.target.value)}
            style={{ padding: '8px 14px', border: '1px solid #1d4ed8', borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', fontWeight: 800, fontSize: 13, cursor: 'pointer', outline: 'none' }}
          >
            <option value="HOSPITAL">🏥 Hospital Revenue (OPD, Lab, Billing)</option>
            <option value="COLLEGE">🎓 College Revenue (Tuition & Student Fees)</option>
            <option value="HOSTEL">🏨 Hostel Revenue (Room Deposit & Fees)</option>
            <option value="ALL">🌐 All Institution Revenue</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* View Toggle */}
          <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden', background: '#f8fafc', padding: 2 }}>
            <button 
              onClick={() => setViewMode('dashboard')}
              style={{ padding: '6px 14px', border: 'none', background: viewMode === 'dashboard' ? '#ffffff' : 'transparent', color: viewMode === 'dashboard' ? '#1d4ed8' : '#64748b', fontWeight: 800, fontSize: 12, borderRadius: 6, cursor: 'pointer', boxShadow: viewMode === 'dashboard' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}
            >
              📊 Analytics View
            </button>
            <button 
              onClick={() => setViewMode('tabular')}
              style={{ padding: '6px 14px', border: 'none', background: viewMode === 'tabular' ? '#ffffff' : 'transparent', color: viewMode === 'tabular' ? '#1d4ed8' : '#64748b', fontWeight: 800, fontSize: 12, borderRadius: 6, cursor: 'pointer', boxShadow: viewMode === 'tabular' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}
            >
              📋 Tabular View ({filteredTxns.length})
            </button>
          </div>

          <button onClick={exportCSV} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#1d4ed8', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            🖨 Export CSV
          </button>
        </div>
      </div>

      {/* VIEW MODE 1: ANALYTICS DASHBOARD VIEW */}
      {viewMode === 'dashboard' && (() => {
        let barItems = [];
        if (revenueCategory === 'HOSPITAL') {
          const labAmt = hospTxns.filter(t => t.id.startsWith('LAB') || t.source.toLowerCase().includes('lab')).reduce((acc, t) => acc + (t.amount_val || 0), 0);
          const opdAmt = hospTxns.filter(t => t.id.startsWith('OPD') || t.source.toLowerCase().includes('opd')).reduce((acc, t) => acc + (t.amount_val || 0), 0);

          barItems = [
            { label: 'Lab Tests', value: labAmt, formattedValue: `₹${labAmt.toLocaleString()}`, color: '#60a5fa' },
            { label: 'OPD Consultations', value: opdAmt, formattedValue: `₹${opdAmt.toLocaleString()}`, color: '#a3e635' }
          ];
        } else if (revenueCategory === 'COLLEGE') {
          const mbbs1 = collTxns.filter(t => (t.source || '').includes('1')).reduce((acc, t) => acc + (t.amount_val || 0), 0);
          const mbbsSenior = collTxns.filter(t => !(t.source || '').includes('1')).reduce((acc, t) => acc + (t.amount_val || 0), 0);

          barItems = [
            { label: 'MBBS 1st Year', value: mbbs1, formattedValue: `₹${mbbs1.toLocaleString()}`, color: '#60a5fa' },
            { label: 'Senior Batches', value: mbbsSenior, formattedValue: `₹${mbbsSenior.toLocaleString()}`, color: '#a3e635' }
          ];
        } else if (revenueCategory === 'HOSTEL') {
          const depAmt = hostTxns.filter(t => (t.source || '').includes('Deposit')).reduce((acc, t) => acc + (t.amount_val || 0), 0);
          const feeAmt = hostTxns.filter(t => !(t.source || '').includes('Deposit')).reduce((acc, t) => acc + (t.amount_val || 0), 0);

          barItems = [
            { label: 'Room Security Deposits', value: depAmt, formattedValue: `₹${depAmt.toLocaleString()}`, color: '#60a5fa' },
            { label: 'Monthly Hostel Fees', value: feeAmt, formattedValue: `₹${feeAmt.toLocaleString()}`, color: '#a3e635' }
          ];
        } else {
          barItems = [
            { label: 'Hospital Revenue', value: hospTotalCollected, formattedValue: `₹${hospTotalCollected.toLocaleString()}`, color: '#60a5fa' },
            { label: 'College Revenue', value: collTotalCollected, formattedValue: `₹${collTotalCollected.toLocaleString()}`, color: '#a3e635' },
            { label: 'Hostel Revenue', value: hostTotalCollected, formattedValue: `₹${hostTotalCollected.toLocaleString()}`, color: '#fbbf24' }
          ];
        }

        const maxVal = Math.max(...barItems.map(b => b.value), 100);
        const step = maxVal / 4;
        const yAxisTicks = [
          '0',
          Math.round(step).toLocaleString(),
          Math.round(step * 2).toLocaleString(),
          Math.round(step * 3).toLocaleString(),
          Math.round(maxVal).toLocaleString()
        ];

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Vertical Bar Chart Dashboard matching user reference image */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', margin: 0 }}>📊 Revenue Distribution & Stream Comparison ({revenueCategory})</h3>
                  <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Compact vertical bar chart generated dynamically from database records</p>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', padding: '4px 12px', borderRadius: 20 }}>
                  Total: {activeData.total_revenue || '₹0'}
                </span>
              </div>

              {/* Chart Container with Y-Axis and Grid Lines */}
              <div style={{ position: 'relative', height: 260, marginTop: 14, display: 'flex' }}>
                {/* Y-Axis Labels */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: 14, fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'right', minWidth: 48, userSelect: 'none' }}>
                  <span>{yAxisTicks[4]}</span>
                  <span>{yAxisTicks[3]}</span>
                  <span>{yAxisTicks[2]}</span>
                  <span>{yAxisTicks[1]}</span>
                  <span>{yAxisTicks[0]}</span>
                </div>

                {/* Chart Area with Horizontal Grid lines and Vertical Bars */}
                <div style={{ flex: 1, position: 'relative', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #cbd5e1', height: 210 }}>
                  {/* Horizontal Grid Lines matching reference image */}
                  {[0, 25, 50, 75, 100].map(pct => (
                    <div key={pct} style={{ position: 'absolute', bottom: `${pct}%`, left: 0, right: 0, height: 1, background: pct === 0 ? 'transparent' : '#f1f5f9' }} />
                  ))}

                  {/* Vertical Bars */}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', padding: '0 16px 4px' }}>
                    {barItems.map((bar, idx) => {
                      const chartHeightPx = 150;
                      const barHeightPx = maxVal > 0 ? Math.max(14, Math.round((bar.value / maxVal) * chartHeightPx)) : 14;
                      return (
                        <div key={bar.label + idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minWidth: 54, position: 'relative' }}>
                          {/* Value Label above Bar */}
                          <span style={{ fontSize: 11, fontWeight: 800, color: '#0f172a', marginBottom: 6, whiteSpace: 'nowrap' }}>
                            {bar.formattedValue}
                          </span>

                          {/* Vertical Colored Bar */}
                          <div 
                            style={{
                              width: 46,
                              height: barHeightPx,
                              minHeight: 14,
                              backgroundColor: bar.color,
                              borderTopLeftRadius: 6,
                              borderTopRightRadius: 6,
                              boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                              transition: 'height 0.4s ease-in-out'
                            }} 
                            title={`${bar.label}: ${bar.formattedValue}`}
                          />

                          {/* X-Axis Label under Bar */}
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginTop: 10, textAlign: 'center', lineHeight: 1.2 }}>
                            {bar.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* VIEW MODE 2: TABULAR VIEW */}
      {viewMode === 'tabular' && (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{revenueCategory} Transaction Log</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="search" placeholder="Search ID, customer, source…" value={txnSearch} onChange={e => setTxnSearch(e.target.value)} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, background: '#fff' }} />
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  <th style={{ padding: '10px 14px' }}>Transaction ID</th>
                  <th style={{ padding: '10px 14px' }}>Date</th>
                  <th style={{ padding: '10px 14px' }}>Customer / Patient / Student</th>
                  <th style={{ padding: '10px 14px' }}>Revenue Source</th>
                  <th style={{ padding: '10px 14px' }}>Amount</th>
                  <th style={{ padding: '10px 14px' }}>Payment Method</th>
                  <th style={{ padding: '10px 14px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxns.length === 0 ? (
                  <tr><td colSpan="7" style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No transaction records found for {revenueCategory} category</td></tr>
                ) : (
                  filteredTxns.map(t => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0f172a' }}>{t.id}</td>
                      <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{t.date}</td>
                      <td style={{ padding: '10px 14px', color: '#334155', fontWeight: 600 }}>{t.customer || t.name}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{t.source || t.course}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0f172a' }}>{t.amount}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{t.method}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534' }}>
                          PAID
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
function AdminSecurityHealthTab({ data }) {
  const [sevFilter, setSevFilter] = useState('ALL');

  const sec = data?.security || {};
  const logs = sec.audit_logs || [];

  const filtered = logs.filter(l => sevFilter === 'ALL' || l.severity === sevFilter);

  const exportLogs = () => {
    if (!filtered.length) {
      notify.info("No security logs available to export.");
      return;
    }
    const content = "data:text/csv;charset=utf-8," 
      + ["Timestamp,User,Action,IP,Severity", ...filtered.map(l => `"${l.time}","${l.user}","${String(l.action).replace(/"/g,'""')}","${l.ip}","${l.severity}"`)].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(content));
    link.setAttribute("download", `Security_Audit_Log_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>🛡 Security Audit & System Performance</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: '3px 0 0' }}>API latency, uptime monitoring, failed login alerts, and administrative logs</p>
        </div>
        <button onClick={exportLogs} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#1d4ed8', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          🛡 Download Audit Log (CSV)
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
        {[
          { label: 'System API Uptime', v: sec.system_uptime || '99.98%', badge: 'Operational', bg: '#dcfce7', c: '#166534' },
          { label: 'Average Response Latency', v: sec.avg_latency || '115 ms', badge: 'Optimal Speed', bg: '#eff6ff', c: '#1d4ed8' },
          { label: 'Failed Logins (24h)', v: `${sec.failed_logins_24h || 0} Attempts`, badge: 'Security Monitor', bg: '#fef3c7', c: '#92400e' },
          { label: 'Email Delivery Rate', v: sec.email_delivery_rate || '99.1%', badge: 'Transactional SMTP Active', bg: '#f1f5f9', c: '#334155' },
        ].map(card => (
          <div key={card.label} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>{card.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '6px 0 4px' }}>{card.v}</div>
            <span style={{ fontSize: 11, fontWeight: 700, background: card.bg, color: card.c, padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>{card.badge}</span>
          </div>
        ))}
      </div>

      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Security & Audit Stream</span>
          <select value={sevFilter} onChange={e => setSevFilter(e.target.value)} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, background: '#fff', cursor: 'pointer' }}>
            <option value="ALL">All Severities</option>
            <option value="INFO">Info</option>
            <option value="WARN">Warning</option>
            <option value="CRITICAL">Critical</option>
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                <th style={{ padding: '10px 14px' }}>Timestamp</th>
                <th style={{ padding: '10px 14px' }}>User / Account</th>
                <th style={{ padding: '10px 14px' }}>Action Performed</th>
                <th style={{ padding: '10px 14px' }}>Severity</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="4" style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No audit logs found</td></tr>
              ) : (
                filtered.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{l.time}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0f172a' }}>{l.user}</td>
                    <td style={{ padding: '10px 14px', color: '#334155' }}>{l.action}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: l.severity==='INFO'?'#eff6ff':l.severity==='WARN'?'#fef3c7':'#fee2e2', color: l.severity==='INFO'?'#1d4ed8':l.severity==='WARN'?'#92400e':'#991b1b' }}>
                        {l.severity}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  NMCDetailModal — calendar + date-filtered class attendance viewer           */
/* ─────────────────────────────────────────────────────────────────────────── */
/*  NMCDetailModal — calendar + date-filtered class attendance viewer           */
/* ─────────────────────────────────────────────────────────────────────────── */
/*  NMCDetailModal — calendar + date-filtered class attendance viewer           */
/* ─────────────────────────────────────────────────────────────────────────── */
function NMCDetailModal({ dept, onClose }) {
  const classes = dept.class_details || [];
  
  // Collect all unique dates that have at least one class
  const classDateSet = new Set(classes.map(c => c.date).filter(d => d !== 'N/A'));
  const classDates   = Array.from(classDateSet).sort();

  // Calendar state
  const today      = new Date();
  const [calYear,  setCalYear]  = useState(classDates.length ? parseInt(classDates[0].slice(0,4)) : today.getFullYear());
  const [calMonth, setCalMonth] = useState(classDates.length ? parseInt(classDates[0].slice(5,7)) - 1 : today.getMonth());
  const [selected, setSelected] = useState(new Set()); // Set of 'YYYY-MM-DD' strings
  const [expanded, setExpanded] = useState({}); // {classId: 'present'|'absent'|null}

  // Calendar helpers
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  const firstDay   = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();

  const fmtDay = d => {
    const mm = String(calMonth + 1).padStart(2,'0');
    const dd = String(d).padStart(2,'0');
    return `${calYear}-${mm}-${dd}`;
  };

  const toggleDate = dateStr => {
    if (!classDateSet.has(dateStr)) return; // only allow days that have classes
    setSelected(prev => {
      const next = new Set(prev);
      next.has(dateStr) ? next.delete(dateStr) : next.add(dateStr);
      return next;
    });
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  };

  // Filter classes by selected dates
  const visibleClasses = selected.size === 0
    ? []
    : classes.filter(c => selected.has(c.date));

  const toggleSection = (id, section) => {
    setExpanded(prev => ({
      ...prev,
      [id]: prev[id] === section ? null : section,
    }));
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      {/* Dynamic responsive CSS rules for mobile support */}
      <style>{`
        .nmc-modal-box {
          background: #f8fafc;
          border-radius: 16px;
          width: 100%;
          max-width: 860px;
          height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 64px rgba(0,0,0,0.18);
          overflow: hidden;
        }
        .nmc-modal-body {
          display: flex;
          flex: 1;
          overflow: hidden;
          min-height: 0;
          flex-direction: row;
        }
        .nmc-calendar-panel {
          width: 260px;
          min-width: 260px;
          flex-shrink: 0;
          background: #ffffff;
          border-right: 1px solid #e2e8f0;
          padding: 18px 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          overflow-y: auto;
        }
        .nmc-cards-panel {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
        }
        @media (max-width: 640px) {
          .nmc-modal-box {
            height: 95vh;
            max-width: 95%;
          }
          .nmc-modal-body {
            flex-direction: column !important;
          }
          .nmc-calendar-panel {
            width: 100% !important;
            min-width: 100% !important;
            border-right: none !important;
            border-bottom: 1px solid #e2e8f0 !important;
            max-height: 330px !important;
            padding: 14px 16px !important;
            gap: 8px !important;
          }
          .nmc-cards-panel {
            flex: 1 !important;
          }
        }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        className="nmc-modal-box"
      >
        {/* ── Modal Header ── */}
        <div style={{ padding: '16px 22px', background: '#ffffff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{dept.name} — Class Attendance</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {dept.scheduled} classes · Avg {dept.avgAtt} · {dept.defaulters} defaulter{dept.defaulters !== 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* ── Body: two-column on desktop, stacked on mobile ── */}
        <div className="nmc-modal-body">

          {/* ── LEFT: Calendar Panel ── */}
          <div className="nmc-calendar-panel">
            <div style={{ width: '100%', maxWidth: 280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Month nav */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#475569', padding: '2px 6px' }}>‹</button>
                <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{MONTHS[calMonth]} {calYear}</span>
                <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#475569', padding: '2px 6px' }}>›</button>
              </div>

              {/* Day-of-week headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center' }}>
                {DAYS.map(d => (
                  <div key={d} style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', padding: '3px 0' }}>{d}</div>
                ))}

                {/* Empty leading cells */}
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}

                {/* Day cells */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day     = i + 1;
                  const dateStr = fmtDay(day);
                  const hasClass  = classDateSet.has(dateStr);
                  const isSel     = selected.has(dateStr);
                  return (
                    <button
                      key={day}
                      onClick={() => toggleDate(dateStr)}
                      title={hasClass ? `${dateStr} — has classes` : ''}
                      style={{
                        border: 'none',
                        borderRadius: 6,
                        padding: '5px 0',
                        fontSize: 11,
                        fontWeight: hasClass ? 700 : 400,
                        cursor: hasClass ? 'pointer' : 'default',
                        background: isSel ? '#3b82f6' : hasClass ? '#eff6ff' : 'transparent',
                        color: isSel ? '#ffffff' : hasClass ? '#1d4ed8' : '#94a3b8',
                        outline: 'none',
                        transition: 'background .15s',
                      }}
                    >{day}</button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4, fontSize: 11, color: '#64748b' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: '#eff6ff', border: '1px solid #bfdbfe', display: 'inline-block' }} />
                  Has classes
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: '#3b82f6', display: 'inline-block' }} />
                  Selected
                </div>
              </div>

              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  style={{ padding: '6px 0', borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', cursor: 'pointer', width: '100%' }}
                >
                  Clear selection ({selected.size})
                </button>
              )}

              {classes.length === 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>No class schedules found.</div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Class cards panel ── */}
          <div className="nmc-cards-panel">
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {selected.size === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: '#94a3b8', gap: 8 }}>
                  <span style={{ fontSize: 32 }}>📅</span>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Select a date on the calendar</div>
                  <div style={{ fontSize: 11 }}>Highlighted dates have scheduled classes</div>
                </div>
              ) : visibleClasses.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: '#94a3b8', gap: 8 }}>
                  <span style={{ fontSize: 28 }}>🔍</span>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>No classes on selected date(s)</div>
                </div>
              ) : (
                visibleClasses.map(cls => {
                  const presentOpen = expanded[cls.id] === 'present';
                  const absentOpen  = expanded[cls.id] === 'absent';
                  const attPct = cls.total_marked > 0
                    ? Math.round((cls.present_count / cls.total_marked) * 100)
                    : null;
                  return (
                    <div key={cls.id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', flexShrink: 0 }}>
                      {/* Class header */}
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>{cls.title}</div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                              <span style={{
                                fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 6,
                                background: (cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#f3e8ff' : '#eff6ff',
                                color: (cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#6b21a8' : '#1d4ed8',
                                border: `1px solid ${(cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '#e9d5ff' : '#bfdbfe'}`
                              }}>
                                Class Type: {(cls.class_type === 'Lab/Practical' || cls.class_type === 'Practical') ? '🔬 Lab/Practical' : '📖 Theory'}
                              </span>
                              {(cls.topic || cls.topic_name) && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#1e40af', background: '#f0f9ff', border: '1px solid #bae6fd', padding: '2px 8px', borderRadius: 6 }}>
                                  Topic Taught: {cls.topic || cls.topic_name}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11.5, color: '#475569', marginTop: 6, fontWeight: 500 }}>
                              👤 Assigned Faculty: <strong style={{ color: '#0f172a' }}>{cls.faculty || cls.assigned_to_name || 'No faculty assigned yet'}</strong>
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                              📅 Date: {cls.date} {cls.time_slot_name ? `· ⏰ Slot: ${cls.time_slot_name}` : ''}
                            </div>
                          </div>
                          {attPct !== null && (
                            <span style={{
                              fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 20, flexShrink: 0,
                              background: attPct >= 75 ? '#dcfce7' : '#fee2e2',
                              color: attPct >= 75 ? '#166534' : '#991b1b'
                            }}>{attPct}%</span>
                          )}
                        </div>

                        {/* Attendance progress bar */}
                        {cls.total_marked > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${(cls.present_count / cls.total_marked) * 100}%`, background: '#22c55e', borderRadius: 4 }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#64748b' }}>
                              <span>{cls.present_count} present</span>
                              <span>{cls.absent_count} absent</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Expandable sections */}
                      <div style={{ display: 'flex', borderTop: '1px solid #f1f5f9' }}>
                        {/* Present toggle */}
                        <button
                          onClick={() => toggleSection(cls.id, 'present')}
                          style={{ flex: 1, padding: '9px 12px', border: 'none', borderRight: '1px solid #f1f5f9', background: presentOpen ? '#f0fdf4' : '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#166534' }}>✓ PRESENT</span>
                          <span style={{ fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#166534', padding: '1px 7px', borderRadius: 20 }}>{cls.present_count}</span>
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{presentOpen ? '▲' : '▼'}</span>
                        </button>
                        {/* Absent toggle */}
                        <button
                          onClick={() => toggleSection(cls.id, 'absent')}
                          style={{ flex: 1, padding: '9px 12px', border: 'none', background: absentOpen ? '#fff7f7' : '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#991b1b' }}>✗ ABSENT</span>
                          <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#991b1b', padding: '1px 7px', borderRadius: 20 }}>{cls.absent_count}</span>
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{absentOpen ? '▲' : '▼'}</span>
                        </button>
                      </div>

                      {/* Present list */}
                      {presentOpen && (
                        <div style={{ padding: '10px 16px 12px', background: '#f0fdf4', borderTop: '1px solid #dcfce7' }}>
                          {cls.present_students.length === 0 ? (
                            <div style={{ fontSize: 11, color: '#64748b' }}>No students marked present.</div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {cls.present_students.map((s, si) => (
                                <span key={si} style={{ fontSize: 11, background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>{s}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Absent list */}
                      {absentOpen && (
                        <div style={{ padding: '10px 16px 12px', background: '#fff7f7', borderTop: '1px solid #fee2e2' }}>
                          {cls.absent_students.length === 0 ? (
                            <div style={{ fontSize: 11, color: '#64748b' }}>No students marked absent.</div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {cls.absent_students.map((s, si) => (
                                <span key={si} style={{ fontSize: 11, background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>{s}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {cls.total_marked === 0 && (
                        <div style={{ padding: '10px 16px', fontSize: 11, color: '#94a3b8' }}>Attendance not yet recorded for this class.</div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function AdminNMCComplianceTab({ data }) {
  const [deptSearch, setDeptSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [detailDept, setDetailDept] = useState(null); // dept object whose classes are shown

  const nmc = data?.nmc || {};
  const depts = nmc.departments || [];

  // Only show rows when user has typed something
  const hasSearch = deptSearch.trim().length > 0;

  const filteredDepts = hasSearch
    ? depts.filter(d => {
        const mSearch = d.name.toLowerCase().includes(deptSearch.toLowerCase());
        const mStatus = statusFilter === 'ALL' || d.status === statusFilter;
        return mSearch && mStatus;
      })
    : [];

  const exportNMC = () => {
    if (!filteredDepts.length) {
      notify.info('Search a department first to export its data.');
      return;
    }
    const csvContent = 'data:text/csv;charset=utf-8,'
      + ['Department,Scheduled Classes,Avg Attendance,Defaulters Count,NMC Status',
          ...filteredDepts.map(d => `"${d.name}",${d.scheduled},"${d.avgAtt}",${d.defaulters},"${d.status}"`)
        ].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `NMC_Inspection_Compliance_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const statusBg  = s => s === 'COMPLIANT' ? '#dcfce7' : s === 'NO CLASSES' ? '#f1f5f9' : '#fef3c7';
  const statusClr = s => s === 'COMPLIANT' ? '#166534' : s === 'NO CLASSES' ? '#475569' : '#92400e';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── View Details Modal ────────────────────────────────────── */}
      {detailDept && (
        <NMCDetailModal dept={detailDept} onClose={() => setDetailDept(null)} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 }}>🏥 NMC Compliance Matrix</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: '3px 0 0' }}>Search a department to view attendance, defaulters and class details</p>
        </div>
        <button onClick={exportNMC} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#1d4ed8', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          📦 Export CSV
        </button>
      </div>

      {/* Table Card */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
            Departmental Attendance &amp; Class Completion
            {hasSearch && filteredDepts.length > 0 && (
              <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>— {filteredDepts.length} result{filteredDepts.length !== 1 ? 's' : ''}</span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="search"
              placeholder="Search department…"
              value={deptSearch}
              onChange={e => setDeptSearch(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, background: '#fff', minWidth: 200 }}
            />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 12, background: '#fff', cursor: 'pointer' }}>
              <option value="ALL">All Statuses</option>
              <option value="COMPLIANT">Compliant</option>
              <option value="ATTENTION">Attention Needed</option>
              <option value="NO CLASSES">No Classes</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                <th style={{ padding: '10px 14px' }}>Department</th>
                <th style={{ padding: '10px 14px' }}>Scheduled Classes</th>
                <th style={{ padding: '10px 14px' }}>Avg Attendance</th>
                <th style={{ padding: '10px 14px' }}>Defaulters (&lt;75%)</th>
                <th style={{ padding: '10px 14px' }}>NMC Status</th>
                <th style={{ padding: '10px 14px' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {!hasSearch ? (
                <tr>
                  <td colSpan="6" style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    🔍 Type a department name above to load compliance data
                  </td>
                </tr>
              ) : filteredDepts.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    No departments match your search
                  </td>
                </tr>
              ) : (
                filteredDepts.map(d => (
                  <tr key={d.name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0f172a' }}>{d.name}</td>
                    <td style={{ padding: '10px 14px', color: '#334155' }}>{d.scheduled}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#0f172a' }}>{d.avgAtt}</td>
                    <td style={{ padding: '10px 14px', color: d.defaulters > 0 ? '#ef4444' : '#334155', fontWeight: d.defaulters > 0 ? 700 : 400 }}>
                      {d.defaulters}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: statusBg(d.status), color: statusClr(d.status) }}>
                        {d.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button
                        onClick={() => setDetailDept(d)}
                        style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────── */
/*  AdminDashboard — main component                                            */
/* ─────────────────────────────────────────────────────────────────────────── */

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({ totalStudents:0, totalFaculty:0, pendingAdmissions:0, activeUsers:0 });
  const [analyticsData,      setAnalyticsData]      = useState(null);
  const [recentUsers,        setRecentUsers]        = useState([]);
  const [admissionRequests,  setAdmissionRequests]  = useState([]);
  const [allUsers,           setAllUsers]           = useState([]);
  const [filteredUsers,      setFilteredUsers]      = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter,  setRoleFilter]  = useState('');
  const [statusFilter,setStatusFilter]= useState('');

  const [showRejectModal,       setShowRejectModal]       = useState(false);
  const [selectedUserId,        setSelectedUserId]        = useState(null);
  const [rejectionReason,       setRejectionReason]       = useState('');
  const [showUserDetailsModal,  setShowUserDetailsModal]  = useState(false);
  const [selectedUserDetails,   setSelectedUserDetails]   = useState(null);
  const [allStudentsList,      setAllStudentsList]      = useState([]);
  const [selectedStudentForActivityModal, setSelectedStudentForActivityModal] = useState(null);
  const [studentSearchQuery,  setStudentSearchQuery]  = useState('');
  const [studentCourseFilter, setStudentCourseFilter] = useState('');
  const [studentDeptBatchFilter, setStudentDeptBatchFilter] = useState('');

  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);
  const [hostelPurchaseRequests, setHostelPurchaseRequests] = useState([]);
  const [hostelLoading, setHostelLoading] = useState(false);
  const [hostelError, setHostelError] = useState(null);
  const [hostelSuccess, setHostelSuccess] = useState(null);

  const fetchHostelPurchaseRequests = async () => {
    try {
      setHostelLoading(true);
      setHostelError(null);
      const data = await hostelInventoryAPI.getHostelPurchaseRequests();
      setHostelPurchaseRequests(data.results || data);
    } catch (err) {
      setHostelError('Failed to load hostel purchase requests');
    } finally {
      setHostelLoading(false);
    }
  };

  const handleApproveHostelPR = async (id, status, reason = '') => {
    try {
      setHostelSuccess(null);
      setHostelError(null);
      await hostelInventoryAPI.updateHostelPurchaseRequest(id, { status, rejection_reason: reason });
      setHostelSuccess(`Hostel purchase request ${status === 'APPROVED_BY_ADMIN' ? 'approved' : 'rejected'} successfully.`);
      fetchHostelPurchaseRequests();
    } catch (err) {
      setHostelError(err.response?.data?.error || 'Failed to update purchase request status');
    }
  };

  // Combine allUsers and dedicated student fetch to ensure 100% of students are retrieved
  const combinedStudents = React.useMemo(() => {
    const map = new Map();
    (allUsers || []).filter(u => u.role === 'STUDENT').forEach(u => map.set(String(u.id), u));
    (allStudentsList || []).forEach(u => map.set(String(u.id), u));
    return Array.from(map.values());
  }, [allUsers, allStudentsList]);

  const getStudentCourseAndYear = (student) => {
    const courseRaw = (student.profile?.course || student.course || student.course_display || '').toUpperCase().trim();
    const yrRaw = student.mbbs_year ?? student.profile?.mbbs_year ?? student.year ?? student.current_mbbs_year;
    const mbbsYear = yrRaw != null ? parseInt(yrRaw, 10) : 0;

    const isPG = ['PG', 'MD', 'MS', 'POSTGRADUATE'].includes(courseRaw) || courseRaw.includes('PG') || courseRaw.includes('MD') || courseRaw.includes('MS');
    const isSS = ['SSPG', 'SUPER_SPECIALITY', 'SUPER SPECIALITY', 'DM', 'MCH', 'SS'].includes(courseRaw) || courseRaw.includes('SUPER') || courseRaw.includes('DM') || courseRaw.includes('MCH');
    const isMBBS = !isPG && !isSS && (courseRaw === '' || courseRaw.includes('MBBS') || courseRaw.includes('MEDICINE') || (mbbsYear >= 1 && mbbsYear <= 4));

    return { courseRaw, mbbsYear, isMBBS, isPG, isSS };
  };

  const matchesStudentFilter = (student, filterKey) => {
    if (!filterKey || filterKey === 'ALL') return true;

    const { mbbsYear, isMBBS, isPG, isSS } = getStudentCourseAndYear(student);

    if (filterKey === 'MBBS_1') {
      return isMBBS && mbbsYear === 1;
    }
    if (filterKey === 'MBBS_2') {
      return isMBBS && mbbsYear === 2;
    }
    if (filterKey === 'MBBS_3') {
      return isMBBS && mbbsYear === 3;
    }
    if (filterKey === 'MBBS_4') {
      return isMBBS && mbbsYear === 4;
    }
    if (filterKey === 'PG') {
      return isPG;
    }
    if (filterKey === 'SSPG') {
      return isSS;
    }
    return true;
  };

  const availableDeptBatches = React.useMemo(() => {
    if (studentCourseFilter !== 'PG' && studentCourseFilter !== 'SSPG') return [];

    const batchSet = new Set();
    combinedStudents.forEach(u => {
      if (!matchesStudentFilter(u, studentCourseFilter)) return;

      const regId = (u.profile?.student_id || u.student_id || u.username || '').trim();
      const m = regId.match(/^([A-Za-z]+)[-]?(\d{2})/);
      if (m) {
        const code = m[1].toUpperCase();
        const yr = m[2];
        batchSet.add(`${code}${yr}`);
      } else if (u.profile?.department_code && (u.profile?.enrollment_year || u.enrollment_year)) {
        const code = u.profile.department_code.toUpperCase();
        const yr = String(u.profile?.enrollment_year || u.enrollment_year).slice(-2);
        batchSet.add(`${code}${yr}`);
      }
    });

    return Array.from(batchSet).sort();
  }, [combinedStudents, studentCourseFilter]);

  const filteredStudents = combinedStudents.filter(u => {
    if (studentSearchQuery) {
      const q = studentSearchQuery.toLowerCase();
      const name = (u.full_name || `${u.first_name || ''} ${u.last_name || ''}`).toLowerCase();
      const email = (u.email || '').toLowerCase();
      const username = (u.username || '').toLowerCase();
      const sId = (u.profile?.student_id || u.student_id || '').toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !username.includes(q) && !sId.includes(q)) return false;
    }

    if (!matchesStudentFilter(u, studentCourseFilter)) return false;

    if ((studentCourseFilter === 'PG' || studentCourseFilter === 'SSPG') && studentDeptBatchFilter) {
      const regId = (u.profile?.student_id || u.student_id || u.username || '').toUpperCase();
      const cleanFilter = studentDeptBatchFilter.toUpperCase();
      const m = regId.match(/^([A-Za-z]+)[-]?(\d{2})/);
      if (m) {
        const prefix = `${m[1]}${m[2]}`;
        if (prefix !== cleanFilter) return false;
      } else {
        if (!regId.startsWith(cleanFilter)) return false;
      }
    }

    return true;
  });

  const handleApprove = async(userId)=>{ if(!await confirmDialog('Approve this user?')) return; try{ await adminAPI.approveUser(userId); await loadDashboardData(); notify.success('User approved.'); }catch(err){ notify.error(err.response?.data?.error||'Failed'); } };
  const handleRejectClick=(userId)=>{ setSelectedUserId(userId); setShowRejectModal(true); };
  const handleCloseModal=()=>{ setShowRejectModal(false); setRejectionReason(''); setSelectedUserId(null); };
  const handleRejectConfirm=async()=>{ try{ await adminAPI.rejectUser(selectedUserId,rejectionReason); handleCloseModal(); await loadDashboardData(); notify.success('User rejected.'); }catch(err){ notify.error(err.response?.data?.error||'Failed'); } };
  const handleDeleteUser=async(userId)=>{ if(!await confirmDialog('Delete this user? Cannot be undone.')) return; try{ await adminAPI.deleteUser(userId); await loadDashboardData(); notify.success('User deleted.'); }catch(err){ notify.error(err.response?.data?.error||'Failed'); } };
  const handleViewUserDetails=(u)=>{ setSelectedUserDetails(u); setShowUserDetailsModal(true); };

  const fmtDate     = s=>s?new Date(s).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}):'N/A';
  const getStBadge  = st=>({ APPROVED:'badge-green',PENDING:'badge-yellow',REJECTED:'badge-red' }[st]||'badge-gray');
  const getRole     = u=>{ if(u.role!=='FACULTY'||!u.profile?.faculty_subrole) return u.role; return {SENIOR_RESIDENT:'Senior Resident',ASSISTANT_PROFESSOR:'Assistant Professor',ASSOCIATE_PROFESSOR:'Associate Professor',PROFESSOR:'Professor',HEAD_OF_DEPARTMENT:'Head of Dept',PROFESSOR_HOD:'Professor+HOD',DEAN:'Dean'}[u.profile.faculty_subrole]||u.role; };
  const getEnrolId  = u=>{ if(u.role==='STUDENT') return u.profile?.student_id||'N/A'; if(['FACULTY','ADMIN','FINANCE','HOSTEL_ADMIN','LIBRARY_STAFF','TRANSPORT_MANAGER'].includes(u.role)) return u.profile?.employee_id||'N/A'; return 'N/A'; };

  useEffect(() => {
    loadDashboardData();
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'hostel-procurement') {
      fetchHostelPurchaseRequests();
    }
    if (activeTab === 'students') {
      mbbsAPI.getStudents().then(mbbsRes => {
        const mbbsList = Array.isArray(mbbsRes) ? mbbsRes : (mbbsRes.results || []);
        if (mbbsList.length > 0) {
          setAllStudentsList(prev => {
            const map = new Map();
            prev.forEach(u => map.set(String(u.id), u));
            mbbsList.forEach(s => {
              const key = String(s.id);
              const existing = map.get(key) || {};
              map.set(key, {
                ...existing,
                ...s,
                id: s.id,
                course: s.course || existing.course || 'MBBS',
                mbbs_year: s.mbbs_year != null ? s.mbbs_year : existing.mbbs_year,
                profile: {
                  ...(existing.profile || {}),
                  student_id: s.student_id || existing.profile?.student_id,
                  course: s.course || existing.profile?.course || 'MBBS',
                  mbbs_year: s.mbbs_year != null ? s.mbbs_year : existing.profile?.mbbs_year,
                  student_department_name: s.department_name || existing.profile?.student_department_name,
                }
              });
            });
            return Array.from(map.values());
          });
        }
      }).catch(() => {});
    }
  }, [activeTab]);
  useEffect(()=>{ applyFilters(); },[allUsers,searchQuery,roleFilter,statusFilter]);
  useEffect(()=>{
    if(showRejectModal) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return ()=>document.body.classList.remove('modal-open');
  },[showRejectModal]);

  const loadDashboardData = async () => {
    try {
      setLoading(true); setError(null);
      const [sd, ru, pd, au, ad, mbbsRes, stdRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getRecentUsers(),
        adminAPI.getPendingAdmissions(),
        adminAPI.getUsers({ page_size: 2000 }),
        adminAPI.getAnalyticsData().catch(() => null),
        mbbsAPI.getStudents().catch(() => []),
        adminAPI.getUsers({ role: 'STUDENT', page_size: 2000 }).catch(() => [])
      ]);
      setStats({totalStudents:sd.total_students||0,totalFaculty:sd.total_faculty||0,pendingAdmissions:sd.pending_admissions||0,activeUsers:sd.active_users||0});
      setRecentUsers(Array.isArray(ru)?ru:(ru.results||[]));
      setAdmissionRequests(Array.isArray(pd)?pd:(pd.results||[]));
      setAllUsers(Array.isArray(au)?au:(au.results||[]));

      const mbbsList = Array.isArray(mbbsRes) ? mbbsRes : (mbbsRes.results || []);
      const stdList = Array.isArray(stdRes) ? stdRes : (stdRes.results || stdRes.data || []);
      const map = new Map();

      (Array.isArray(au) ? au : (au.results || [])).filter(u => u.role === 'STUDENT').forEach(u => {
        map.set(String(u.id), { ...u });
      });

      stdList.forEach(u => {
        const key = String(u.id);
        const existing = map.get(key) || {};
        map.set(key, { ...existing, ...u });
      });

      mbbsList.forEach(s => {
        const key = String(s.id);
        const existing = map.get(key) || {};
        map.set(key, {
          ...existing,
          ...s,
          id: s.id,
          username: s.username || existing.username,
          email: s.email || existing.email,
          full_name: s.full_name || existing.full_name,
          first_name: s.first_name || existing.first_name,
          last_name: s.last_name || existing.last_name,
          course: s.course || existing.course || 'MBBS',
          mbbs_year: s.mbbs_year != null ? s.mbbs_year : existing.mbbs_year,
          profile: {
            ...(existing.profile || {}),
            student_id: s.student_id || existing.profile?.student_id,
            course: s.course || existing.profile?.course || 'MBBS',
            mbbs_year: s.mbbs_year != null ? s.mbbs_year : existing.profile?.mbbs_year,
            student_department_name: s.department_name || existing.profile?.student_department_name,
          }
        });
      });

      setAllStudentsList(Array.from(map.values()));
      setAnalyticsData(ad);
    } catch(err) {
      setError('Failed to load dashboard data.'); setRecentUsers([]); setAdmissionRequests([]); setAllUsers([]); setAllStudentsList([]);
    } finally { setLoading(false); }
  };

  const applyFilters = () => {
    let f = Array.isArray(allUsers)?[...allUsers]:[];
    if(searchQuery){ const q=searchQuery.toLowerCase(); f=f.filter(u=>[u.username,u.email,u.full_name,u.first_name,u.last_name,u.profile?.student_id,u.profile?.employee_id].some(x=>x?.toLowerCase().includes(q))); }
    if(roleFilter){ const subs=['SENIOR_RESIDENT','ASSISTANT_PROFESSOR','ASSOCIATE_PROFESSOR','PROFESSOR','HEAD_OF_DEPARTMENT','PROFESSOR_HOD','DEAN']; if(subs.includes(roleFilter)) f=f.filter(u=>u.role==='FACULTY'&&u.profile?.faculty_subrole===roleFilter); else f=f.filter(u=>u.role===roleFilter); }
    if(statusFilter) f=f.filter(u=>u.approval_status===statusFilter);
    setFilteredUsers(f);
  };

  if(loading&&!stats.totalStudents&&allUsers.length===0){
    return <DashboardLayout title="Admin Dashboard"><div className="loading-container"><div className="spinner-large"/><p>Loading…</p></div></DashboardLayout>;
  }

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="dashboard-container">
        {error&&<div className="error-alert"><svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>{error}</div>}

        {/* Stat cards */}
        <div className="stats-grid">
          <div onClick={()=>setActiveTab('students')} style={{cursor:'pointer'}}>
            <StatCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>} title="Total Students" value={stats.totalStudents} color="blue"/>
          </div>
          <div onClick={()=>setActiveTab('users')} style={{cursor:'pointer'}}>
            <StatCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>} title="Total Faculty" value={stats.totalFaculty} color="green"/>
          </div>
          <div onClick={()=>setActiveTab('admissions')} style={{cursor:'pointer'}}>
            <StatCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>} title="Pending Admissions" value={stats.pendingAdmissions} color="orange"/>
          </div>
          <div onClick={()=>setActiveTab('promote-students')} style={{cursor:'pointer'}}>
            <StatCard icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg>} title="Active Users" value={stats.activeUsers} color="purple"/>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="tabs-container">
          <div className="tabs" style={{flexWrap:'wrap',gap:4}}>
            {[
              ['overview',          'Overview'],
              ['users',             'User Management'],
              ['students',          '🎓 Students'],
              ['admissions',        `Admissions (${stats.pendingAdmissions})`],
              ['finance-analytics', '💳 Financial Analytics'],
              ['system-security',   '🛡 Security & Health'],
              ['nmc-compliance',    '🏥 NMC Compliance'],
              ['promote-students',  '🎓 Promote Students'],
              ['cm-permissions',    '🔑 Ethics Committee'],
              ['approve-leaves',    'Approve Leaves'],
              ['leave-policies',    'Leave Policies'],
              ['calendar',          '📅 Calendar'],
              ['shifts',            '🕐 Shifts'],
              ['settings',          '⚙ Settings'],
              ['library',           '📚 My Library'],
              ...(hasHostelAllocation ? [
                ['hostel',          '🏨 Hostel']
              ] : []),
              ['hostel-procurement', '🏨 Hostel Procurement'],
            ].map(([key,label])=>(
              <button key={key} className={`tab ${activeTab===key?'active':''}`} onClick={()=>setActiveTab(key)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="tab-content">

          {/* Overview */}
          {activeTab==='overview'&&(
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Top Unified Executive Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total Active Users</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '6px 0 4px' }}>{stats.activeUsers || 0}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>System Active</span>
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Tuition Fee Realization</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '6px 0 4px' }}>{analyticsData?.financials?.total_tuition_collected || '₹0'}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>{analyticsData?.financials?.total_tuition_pending ? `${analyticsData.financials.total_tuition_pending} Pending` : 'Realized Collections'}</span>
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Security & System Uptime</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '6px 0 4px' }}>{analyticsData?.security?.system_uptime || '100%'}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>{analyticsData?.security?.failed_logins_24h || 0} Failed Logins</span>
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>NMC Faculty-Student Ratio</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '6px 0 4px' }}>{analyticsData?.nmc?.faculty_student_ratio || '1 : 1'}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>{analyticsData?.nmc?.defaulter_count || 0} Overdue/Defaulters</span>
                </div>
              </div>

              <div className="grid-2-col">
                <div className="card">
                  <div className="card-header"><h2>Recent Users</h2><button className="btn-link" onClick={()=>setActiveTab('users')}>View All</button></div>
                  <div className="card-body">
                    {recentUsers.length===0?<p className="text-center text-gray">No users found</p>:recentUsers.map(u=>(
                      <div key={u.id} className="list-item">
                        <div className="list-item-icon user-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg></div>
                        <div className="list-item-content"><h4>{u.full_name||`${u.first_name} ${u.last_name}`||u.username}</h4><p>{u.email} • {getRole(u)}</p></div>
                        <span className={`badge ${getStBadge(u.approval_status)}`}>{u.approval_status}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card">
                  <div className="card-header"><h2>Pending Admissions</h2><button className="btn-link" onClick={()=>setActiveTab('admissions')}>View All</button></div>
                  <div className="card-body">
                    {admissionRequests.length===0?<p className="text-center text-gray">No pending admissions</p>:admissionRequests.slice(0,5).map(r=>(
                      <div key={r.id} className="list-item">
                        <div className="list-item-icon admission-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg></div>
                        <div className="list-item-content"><h4>{r.full_name||`${r.first_name} ${r.last_name}`||r.username}</h4><p>{r.profile?.course||getRole(r)} • {r.profile?.entrance_score?`Score: ${r.profile.entrance_score}`:'No score'}</p></div>
                        <span className="badge badge-orange">{r.approval_status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Financial Analytics */}
          {activeTab==='finance-analytics' && <AdminFinancialAnalyticsTab data={analyticsData} />}

          {/* Security & Health */}
          {activeTab==='system-security' && <AdminSecurityHealthTab data={analyticsData} />}

          {/* NMC Compliance */}
          {activeTab==='nmc-compliance' && <AdminNMCComplianceTab data={analyticsData} />}

          {/* User Management */}
          {activeTab==='users'&&(
            <div className="card">
              <div className="card-header"><h2>User Management</h2></div>
              <div className="card-body">
                <div className="table-filters">
                  <input type="search" placeholder="Search users..." className="search-input" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}/>
                  <select className="filter-select" value={roleFilter} onChange={e=>setRoleFilter(e.target.value)}>
                    <option value="">All Roles</option>
                    <optgroup label="Main Roles">{['STUDENT','ADMIN','FINANCE','HOSTEL_ADMIN','LIBRARY_STAFF','TRANSPORT_MANAGER'].map(r=><option key={r} value={r}>{r}</option>)}</optgroup>
                    <optgroup label="Faculty Sub-Roles">{['SENIOR_RESIDENT','ASSISTANT_PROFESSOR','ASSOCIATE_PROFESSOR','PROFESSOR','HEAD_OF_DEPARTMENT','PROFESSOR_HOD','DEAN'].map(r=><option key={r} value={r}>{r.replace(/_/g,' ')}</option>)}</optgroup>
                  </select>
                  <select className="filter-select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
                    <option value="">All Status</option>
                    {['APPROVED','PENDING','REJECTED'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Enrollment ID</th><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
                    <tbody>
                      {filteredUsers.length===0?<tr><td colSpan="7" className="text-center">No users found</td></tr>:filteredUsers.map(u=>(
                        <tr key={u.id}>
                          <td><strong>{getEnrolId(u)}</strong></td>
                          <td><strong>{u.full_name||`${u.first_name} ${u.last_name}`||u.username}</strong></td>
                          <td>{u.email}</td>
                          <td><span className="badge badge-blue">{getRole(u)}</span></td>
                          <td><span className={`badge ${getStBadge(u.approval_status)}`}>{u.approval_status}</span></td>
                          <td>{fmtDate(u.created_at)}</td>
                          <td><div className="action-buttons">
                            <button className="btn-sm btn-outline" onClick={()=>handleViewUserDetails(u)}>View</button>
                            {u.role!=='ADMIN'&&<button className="btn-sm btn-danger" onClick={()=>handleDeleteUser(u.id)}>Delete</button>}
                          </div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Students */}
          {activeTab === 'students' && (
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h2 style={{ margin: 0 }}>Student Roster & Activities</h2>
                  <p className="card-subtitle" style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                    Showing {filteredStudents.length} of {combinedStudents.length} total enrolled students
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="filter-select"
                    value={studentCourseFilter}
                    onChange={e => {
                      setStudentCourseFilter(e.target.value);
                      setStudentDeptBatchFilter('');
                    }}
                    style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', color: '#0f172a', fontWeight: 600 }}
                  >
                    <option value="">All Programs / Years</option>
                    <option value="MBBS_1">MBBS 1st Year</option>
                    <option value="MBBS_2">MBBS 2nd Year</option>
                    <option value="MBBS_3">MBBS 3rd Year</option>
                    <option value="MBBS_4">MBBS 4th Year</option>
                    <option value="PG">PG (MD / MS)</option>
                    <option value="SSPG">SSPG (DM / MCh)</option>
                  </select>

                  {(studentCourseFilter === 'PG' || studentCourseFilter === 'SSPG') && (
                    <select
                      className="filter-select"
                      value={studentDeptBatchFilter}
                      onChange={e => setStudentDeptBatchFilter(e.target.value)}
                      style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #3b82f6', fontSize: 13, background: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
                    >
                      <option value="">All Batches & Depts</option>
                      {availableDeptBatches.map(batch => (
                        <option key={batch} value={batch}>
                          {batch} Batch
                        </option>
                      ))}
                    </select>
                  )}

                  <input
                    type="search"
                    placeholder="🔍 Search name, student ID, or email..."
                    className="search-input"
                    value={studentSearchQuery}
                    onChange={e => setStudentSearchQuery(e.target.value)}
                    style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, minWidth: 240 }}
                  />
                </div>
              </div>

              <div className="card-body">
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Department</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.length === 0 ? (
                        <tr><td colSpan="5" className="text-center" style={{ padding: 40, color: '#94a3b8' }}>No students found matching your search and filter criteria.</td></tr>
                      ) : (
                        filteredStudents.map(student => {
                          const sId = student.profile?.student_id || student.student_id || student.username || 'N/A';
                          const sName = student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || student.username;
                          const deptDisplay = student.profile?.student_department_name || student.department_name || student.profile?.student_department?.name || 'General';

                          return (
                            <tr key={student.id}>
                              <td><strong>{sId}</strong></td>
                              <td><strong>{sName}</strong></td>
                              <td>{student.email}</td>
                              <td>{deptDisplay}</td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="btn-sm btn-info"
                                    style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                    onClick={() => setSelectedStudentForActivityModal(student)}
                                  >
                                    View Activities
                                  </button>
                                  <button
                                    className="btn-sm btn-outline"
                                    onClick={() => handleViewUserDetails(student)}
                                  >
                                    View Details
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Admissions */}
          {activeTab==='admissions'&&(
            <div className="card">
              <div className="card-header"><h2>Admission Requests</h2></div>
              <div className="card-body">
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Applicant</th><th>Email</th><th>Role/Course</th><th>Score</th><th>Applied</th><th>Actions</th></tr></thead>
                    <tbody>
                      {admissionRequests.length===0?<tr><td colSpan="6" className="text-center">No pending admissions</td></tr>:admissionRequests.map(r=>(
                        <tr key={r.id}>
                          <td><strong>{r.full_name||`${r.first_name} ${r.last_name}`||r.username}</strong></td>
                          <td>{r.email}</td>
                          <td>{r.profile?.course||getRole(r)}</td>
                          <td>{r.profile?.entrance_score?<strong>{r.profile.entrance_score}%</strong>:'N/A'}</td>
                          <td>{fmtDate(r.created_at)}</td>
                          <td><div className="action-buttons">
                            <button className="btn-sm btn-success" onClick={()=>handleApprove(r.id)}>Approve</button>
                            <button className="btn-sm btn-danger" onClick={()=>handleRejectClick(r.id)}>Reject</button>
                          </div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── NEW: Promote Students tab ─────────────────────────────────── */}
          {activeTab==='promote-students'&&(
            <div className="card">
              <div className="card-header">
                <div>
                  <h2 style={{margin:0}}>Promote MBBS Students</h2>
                  <p className="card-subtitle" style={{margin:'4px 0 0',fontSize:13,color:'#6b7280'}}>
                    Select students and promote them to the next academic year.
                    Departments update automatically on promotion.
                  </p>
                </div>
              </div>
              <div className="card-body" style={{padding:20}}>
                <PromoteStudents />
              </div>
            </div>
          )}

          {/* CM Permissions */}
          {activeTab==='cm-permissions'&&(
            <div className="card">
              <div className="card-body" style={{padding:20}}>
                <AdminCMPermissions />
              </div>
            </div>
          )}

          {/* Existing tabs — unchanged */}
          {activeTab==='approve-leaves'   && <div className="card"><div className="card-header"><h2>HOD Leave Approvals</h2></div><div className="card-body"><AdminLeaveApproval/></div></div>}
          {activeTab==='leave-policies'   && <div className="card"><AdminLeavePolicyManager/></div>}
          {activeTab==='calendar'         && <div className="card" style={{padding:0,overflow:'visible'}}><Calendar/></div>}
          {activeTab==='shifts'           && <div className="card"><div className="card-header"><h2>Shift Management</h2></div><div className="card-body" style={{padding:20}}><ShiftManagement/></div></div>}
          {activeTab==='settings'         && <div className="card" style={{padding:0,overflow:'visible'}}><AdminSettings/></div>}
          {activeTab==='library'          && <MyBorrowedBooks />}
          {activeTab==='hostel'           && <HostelUserPortalTab />}

          {activeTab==='hostel-procurement' && (
            <div className="card">
              <div className="card-header">
                <h2>Hostel Procurement Approvals</h2>
              </div>
              <div className="card-body">
                {hostelSuccess && <div className="success-alert" style={{ marginBottom: '15px' }}>{hostelSuccess}</div>}
                {hostelError && <div className="error-alert" style={{ marginBottom: '15px' }}>{hostelError}</div>}
                
                {hostelLoading ? (
                  <div className="text-center" style={{ padding: '20px' }}>Loading requests...</div>
                ) : hostelPurchaseRequests.length === 0 ? (
                  <div className="text-center text-gray" style={{ padding: '20px' }}>No procurement requests found.</div>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Request ID</th>
                          <th>Hostel & Room</th>
                          <th>Accessory Type</th>
                          <th>Qty</th>
                          <th>Justification & Priority</th>
                          <th>Status</th>
                          <th>Vendor / Cost Detail</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hostelPurchaseRequests.map(hpr => (
                          <tr key={hpr.id}>
                            <td>
                              <strong>HPR-{hpr.id}</strong>
                              {hpr.complaint && <div style={{ fontSize: '10px', color: '#6b7280' }}>Complaint #{hpr.complaint}</div>}
                            </td>
                            <td>
                              <strong>Room {hpr.room_number || 'N/A'}</strong>
                              <div style={{ fontSize: '11px', color: '#4b5563' }}>{hpr.hostel_name || 'Hostel'}</div>
                            </td>
                            <td>
                              <span style={{ fontWeight: 'bold', color: '#4f46e5' }}>{hpr.accessory_type_display}</span>
                              {hpr.existing_asset_label && <div style={{ fontSize: '10px', color: '#dc2626' }}>Replacing: {hpr.existing_asset_label}</div>}
                            </td>
                            <td>{hpr.quantity}</td>
                            <td>
                              <div style={{ maxWidth: '250px', whiteSpace: 'normal', fontSize: '12px' }}>
                                <p style={{ margin: 0, fontStyle: 'italic' }}>"{hpr.reason}"</p>
                                <span className={`badge ${hpr.priority === 'HIGH' ? 'badge-red' : hpr.priority === 'MEDIUM' ? 'badge-orange' : 'badge-blue'}`} style={{ marginTop: '4px' }}>
                                  {hpr.priority_display}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge" style={{
                                background: (hpr.status === 'REQUESTED' || hpr.status === 'PENDING') ? '#f59e0b' :
                                            hpr.status === 'APPROVED_BY_ADMIN' ? '#3b82f6' :
                                            hpr.status === 'PURCHASED' ? '#10b981' :
                                            hpr.status === 'INSTALLED' ? '#047857' : '#ef4444',
                                color: '#fff',
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                              }}>
                                {hpr.status_display}
                              </span>
                            </td>
                            <td>
                              {hpr.status === 'PURCHASED' || hpr.status === 'INSTALLED' ? (
                                <div style={{ fontSize: '12px' }}>
                                  <strong>₹{hpr.purchase_cost}</strong> via {hpr.vendor_name}
                                  <br/>
                                  <span style={{ fontSize: '10px', color: '#6b7280' }}>Invoice: {hpr.invoice_number}</span>
                                </div>
                              ) : (
                                <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>Awaiting Purchase</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '5px' }}>
                                {(hpr.status === 'REQUESTED' || hpr.status === 'PENDING') && (
                                  <>
                                    <button className="btn-sm btn-success" onClick={() => handleApproveHostelPR(hpr.id, 'APPROVED_BY_ADMIN')}>
                                      Approve
                                    </button>
                                    <button className="btn-sm btn-danger" onClick={() => {
                                      const reason = prompt('Enter rejection reason:');
                                      if (reason !== null) handleApproveHostelPR(hpr.id, 'REJECTED', reason);
                                    }}>
                                      Reject
                                    </button>
                                  </>
                                )}
                                {hpr.status === 'APPROVED_BY_ADMIN' && (
                                  <span style={{ fontSize: '11px', color: '#3b82f6', fontStyle: 'italic' }}>
                                    Awaiting Finance Purchase
                                  </span>
                                )}
                                {hpr.status === 'PURCHASED' && (
                                  <span style={{ fontSize: '11px', color: '#10b981', fontStyle: 'italic' }}>
                                    Purchased, Awaiting Installation
                                  </span>
                                )}
                                {hpr.status === 'INSTALLED' && (
                                  <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 'bold' }}>
                                    Installed
                                  </span>
                                )}
                                {hpr.status === 'REJECTED' && (
                                  <span style={{ fontSize: '11px', color: '#dc2626', fontStyle: 'italic' }}>
                                    Rejected
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Reject modal */}
        {showRejectModal&&(
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={e=>e.stopPropagation()}>
              <div className="modal-header"><h3>Reject Admission</h3><button className="modal-close" onClick={handleCloseModal}>×</button></div>
              <div className="modal-body">
                <label>Rejection Reason (Optional)</label>
                <textarea className="form-textarea" rows="4" placeholder="Enter reason…" value={rejectionReason} onChange={e=>setRejectionReason(e.target.value)}/>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleCloseModal}>Cancel</button>
                <button className="btn-danger" onClick={handleRejectConfirm}>Reject User</button>
              </div>
            </div>
          </div>
        )}

        {showUserDetailsModal && selectedUserDetails && (
          <PrincipalUserModal
            userId={selectedUserDetails.id || selectedUserDetails.user_id || selectedUserDetails.user}
            onClose={() => {
              setShowUserDetailsModal(false);
              setSelectedUserDetails(null);
            }}
          />
        )}

        {selectedStudentForActivityModal && (
          <StudentActivityModal
            student={{
              ...selectedStudentForActivityModal,
              id: selectedStudentForActivityModal.id || selectedStudentForActivityModal.user_id || selectedStudentForActivityModal.user,
              name: selectedStudentForActivityModal.full_name || `${selectedStudentForActivityModal.first_name || ''} ${selectedStudentForActivityModal.last_name || ''}`.trim() || selectedStudentForActivityModal.username,
              student_id: selectedStudentForActivityModal.profile?.student_id || selectedStudentForActivityModal.student_id || selectedStudentForActivityModal.username,
              course: selectedStudentForActivityModal.profile?.course || selectedStudentForActivityModal.course || 'MBBS',
              department: selectedStudentForActivityModal.profile?.student_department_name || selectedStudentForActivityModal.department_name || '',
            }}
            onClose={() => setSelectedStudentForActivityModal(null)}
            user={user}
            fromSource="Principal"
          />
        )}
      </div>
      <style>{BHM_CSS}</style>
    </DashboardLayout>
  );
};

/* ─── UserDetailsModal (unchanged) ──────────────────────────────────────── */
const UserDetailsModal = ({ user, onClose }) => {
  const getFS = s=>({SENIOR_RESIDENT:'Senior Resident',ASSISTANT_PROFESSOR:'Assistant Professor',ASSOCIATE_PROFESSOR:'Associate Professor',PROFESSOR:'Professor',HEAD_OF_DEPARTMENT:'Head of Department',PROFESSOR_HOD:'Professor + HOD',DEAN:'Dean'}[s]||s);
  const getG  = g=>({M:'Male',F:'Female',O:'Other'}[g]||g);
  const fD    = s=>s?new Date(s).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}):'N/A';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><h2>User Details</h2><button className="modal-close" onClick={onClose}>&times;</button></div>
        <div className="modal-body">
          <div className="professor-details-grid">
            <div className="details-section">
              <h3 className="section-title">Personal Information</h3>
              <div className="details-list">
                {[['Full Name',`${user.first_name} ${user.last_name}`],['Username',user.username],['Email',user.email],['Phone',user.phone_number||'Not provided']].map(([l,v])=><div key={l} className="detail-item"><span className="detail-label">{l}:</span><span className="detail-value">{v}</span></div>)}
                {user.profile?.date_of_birth&&<div className="detail-item"><span className="detail-label">DOB:</span><span className="detail-value">{fD(user.profile.date_of_birth)}</span></div>}
                {user.profile?.gender       &&<div className="detail-item"><span className="detail-label">Gender:</span><span className="detail-value">{getG(user.profile.gender)}</span></div>}
                {user.profile?.address      &&<div className="detail-item full-width"><span className="detail-label">Address:</span><span className="detail-value">{user.profile.address}</span></div>}
              </div>
            </div>
            {user.role==='STUDENT'&&(
              <div className="details-section">
                <h3 className="section-title">Academic Information</h3>
                <div className="details-list">
                  {[['Student ID',user.profile?.student_id||'N/A'],['Course',user.profile?.course||'N/A']].map(([l,v])=><div key={l} className="detail-item"><span className="detail-label">{l}:</span><span className="detail-value">{v}</span></div>)}
                  {user.profile?.course==='MBBS'&&user.profile?.mbbs_year&&<div className="detail-item"><span className="detail-label">MBBS Year:</span><span className="detail-value">{['','1st Year','2nd Year','3rd Year','4th Year'][user.profile.mbbs_year]||user.profile.mbbs_year}</span></div>}
                  {user.profile?.assigned_professor_name&&<div className="detail-item"><span className="detail-label">Assigned Professor:</span><span className="detail-value">{user.profile.assigned_professor_name}</span></div>}
                </div>
              </div>
            )}
            {['FACULTY','ADMIN','FINANCE','HOSTEL_ADMIN','LIBRARY_STAFF','TRANSPORT_MANAGER'].includes(user.role)&&(
              <div className="details-section">
                <h3 className="section-title">Professional Information</h3>
                <div className="details-list">
                  <div className="detail-item"><span className="detail-label">Employee ID:</span><span className="detail-value">{user.profile?.employee_id||'N/A'}</span></div>
                  <div className="detail-item"><span className="detail-label">Role:</span><span className="badge badge-blue">{user.role}</span></div>
                  {user.role==='FACULTY'&&user.profile?.faculty_subrole&&<div className="detail-item"><span className="detail-label">Sub-Role:</span><span className="detail-value">{getFS(user.profile.faculty_subrole)}</span></div>}
                  {user.profile?.faculty_department_name&&<div className="detail-item"><span className="detail-label">Department:</span><span className="detail-value">{user.profile.faculty_department_name}</span></div>}
                  {user.profile?.joining_date&&<div className="detail-item"><span className="detail-label">Joining Date:</span><span className="detail-value">{fD(user.profile.joining_date)}</span></div>}
                </div>
              </div>
            )}
            <div className="details-section full-width">
              <h3 className="section-title">Account Status</h3>
              <div className="details-list">
                <div className="detail-item"><span className="detail-label">Status:</span><span className={`badge badge-${user.approval_status==='APPROVED'?'green':user.approval_status==='PENDING'?'yellow':'red'}`}>{user.approval_status}</span></div>
                <div className="detail-item"><span className="detail-label">Created:</span><span className="detail-value">{fD(user.created_at)}</span></div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

/* ─── BHM CSS (unchanged) ────────────────────────────────────────────────── */
const BHM_CSS = `
.bulk-holiday-manager{padding:20px;display:flex;flex-direction:column;gap:24px}
.bhm-year-bar{display:flex;align-items:center;gap:12px}.bhm-year-label{font-size:.85rem;color:#64748b;font-weight:500}
.bhm-year-nav{display:flex;align-items:center;gap:6px}.bhm-year-btn{background:#f1f5f9;border:1px solid #e2e8f0;border-radius:6px;width:28px;height:28px;cursor:pointer;font-size:1rem;color:#475569}
.bhm-year-value{font-size:1.05rem;font-weight:700;color:#1e293b;min-width:42px;text-align:center}
.bhm-result-banner{position:relative;border-radius:8px;padding:12px 38px 12px 14px;border:1px solid}
.bhm-result-banner.bhm-success{background:#f0fdf4;border-color:#86efac}.bhm-result-banner.bhm-partial{background:#fffbeb;border-color:#fcd34d}
.bhm-result-summary{display:flex;gap:14px;flex-wrap:wrap}.bhm-result-ok{color:#15803d;font-weight:600;font-size:.88rem}.bhm-result-err{color:#b45309;font-weight:600;font-size:.88rem}
.bhm-error-list{margin-top:6px;display:flex;flex-direction:column;gap:2px}.bhm-error-item{font-size:.8rem;color:#92400e}
.bhm-result-close{position:absolute;top:10px;right:10px;background:none;border:none;font-size:1.1rem;cursor:pointer;color:#94a3b8}
.bhm-section{background:#fff;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden}
.bhm-section-header{display:flex;align-items:flex-start;justify-content:space-between;padding:14px 18px 10px;border-bottom:1px solid #f1f5f9;gap:12px;flex-wrap:wrap}
.bhm-section-title{font-size:.97rem;font-weight:700;color:#0f172a;margin:0 0 2px}.bhm-section-desc{font-size:.79rem;color:#64748b;margin:0}
.bhm-section-actions{display:flex;gap:8px}.bhm-btn-ghost{background:#f8fafc;border:1px solid #e2e8f0;color:#475569;padding:5px 11px;border-radius:7px;font-size:.79rem;cursor:pointer;font-weight:500}
.bhm-table-header{display:grid;grid-template-columns:24px 110px 1fr 148px 1fr 108px 30px;gap:8px;padding:7px 16px;background:#f8fafc;border-bottom:1px solid #f1f5f9;font-size:.71rem;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:.04em;align-items:center}
.bhm-req{color:#ef4444}
.bhm-rows{display:flex;flex-direction:column}.bhm-row{display:grid;grid-template-columns:24px 110px 1fr 148px 1fr 108px 30px;gap:8px;padding:6px 16px;border-bottom:1px solid #f8fafc;align-items:center}
.bhm-row:hover{background:#fafafa}.bhm-row.bhm-row-valid{background:#f0fdf4!important}.bhm-row-num{font-size:.72rem;color:#94a3b8;text-align:center}
.bhm-input{border:1px solid #e2e8f0;border-radius:7px;padding:6px 8px;font-size:.82rem;color:#1e293b;outline:none;background:#fff;width:100%;box-sizing:border-box}
.bhm-input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.1)}
.bhm-select{border:1px solid #e2e8f0;border-left-width:3px;border-radius:7px;padding:6px 7px;font-size:.82rem;color:#1e293b;outline:none;background:#fff;width:100%;box-sizing:border-box;cursor:pointer}
.bhm-checkbox-wrap{display:flex;align-items:center;gap:5px;cursor:pointer}.bhm-checkbox-wrap input[type=checkbox]{width:14px;height:14px;accent-color:#2563eb}
.bhm-rec-label{font-size:.78rem;color:#475569;white-space:nowrap}
.bhm-del-row{background:none;border:none;color:#cbd5e1;cursor:pointer;font-size:.9rem;padding:3px 5px;border-radius:5px}.bhm-del-row:hover:not(:disabled){color:#ef4444;background:#fee2e2}.bhm-del-row:disabled{opacity:.3;cursor:not-allowed}
.bhm-footer{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-top:1px solid #f1f5f9;background:#fafafa;flex-wrap:wrap;gap:10px}
.bhm-btn-add-row{background:none;border:1px dashed #cbd5e1;color:#64748b;padding:6px 13px;border-radius:7px;font-size:.82rem;cursor:pointer}
.bhm-footer-right{display:flex;align-items:center;gap:12px}.bhm-valid-count{font-size:.8rem;color:#64748b}
.bhm-btn-save{background:#2563eb;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:.88rem;font-weight:600;cursor:pointer}.bhm-btn-save:disabled{opacity:.55;cursor:not-allowed}
.bhm-list-loading{display:flex;align-items:center;justify-content:center;gap:10px;padding:36px;color:#64748b}.bhm-spinner{width:20px;height:20px;border:3px solid #e2e8f0;border-top-color:#2563eb;border-radius:50%;animation:bhm-spin .7s linear infinite}
@keyframes bhm-spin{to{transform:rotate(360deg)}}.bhm-empty{padding:36px;text-align:center;color:#94a3b8}
.bhm-existing-header{display:grid;grid-template-columns:142px 1fr 162px 110px 164px;gap:8px;padding:7px 18px;background:#f8fafc;border-bottom:1px solid #f1f5f9;font-size:.71rem;font-weight:600;color:#94a3b8;text-transform:uppercase}
.bhm-existing-list{display:flex;flex-direction:column}.bhm-existing-row{display:grid;grid-template-columns:142px 1fr 162px 110px 164px;gap:8px;padding:8px 18px;border-bottom:1px solid #f8fafc;align-items:center;font-size:.83rem;color:#334155}
.bhm-existing-row:last-child{border-bottom:none}.bhm-existing-row:hover{background:#fafafa}.bhm-editing-row{background:#eff6ff!important}
.bhm-date-chip{display:flex;align-items:center;gap:6px;font-family:monospace;font-size:.81rem}.bhm-type-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.bhm-name-cell{font-weight:500}.bhm-type-label{display:inline-block;padding:2px 8px;border-radius:99px;font-size:.73rem;font-weight:600}
.bhm-rec-badge{font-size:.74rem;color:#7c3aed;background:#ede9fe;padding:2px 7px;border-radius:99px}.bhm-once-badge{font-size:.74rem;color:#94a3b8}
.bhm-row-actions{display:flex;gap:6px;align-items:center}.bhm-action-btn{border:none;padding:4px 9px;border-radius:6px;font-size:.75rem;cursor:pointer;font-weight:600}
.bhm-edit-btn{background:#dbeafe;color:#1d4ed8}.bhm-edit-btn:hover{background:#bfdbfe}.bhm-delete-btn{background:#fee2e2;color:#b91c1c}.bhm-delete-btn:hover{background:#fecaca}
.bhm-edit-actions{display:flex;gap:6px;align-items:center}.bhm-btn-save-inline{background:#dcfce7;color:#166534;border:none;padding:5px 10px;border-radius:6px;font-size:.77rem;font-weight:700;cursor:pointer}
.bhm-btn-cancel-inline{background:#f1f5f9;color:#64748b;border:none;padding:5px 10px;border-radius:6px;font-size:.77rem;cursor:pointer}
`;

export default AdminDashboard;